import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CornersOut, CornersIn } from "@phosphor-icons/react";

/**
 * Fullscreen toggle — uses the Fullscreen API. Renders as a small icon
 * button; on browsers that don't support the API (or block it — some
 * mobile Safari builds) we simply don't render.
 *
 * The button is hidden when the app is already running in a PWA
 * standalone context, because the OS is already giving us the full
 * screen and the Fullscreen API rarely does anything useful there.
 */
const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: fullscreen)").matches ||
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator?.standalone === true
  );
};

export const FullscreenToggle = ({ className = "", ...rest }) => {
  const [isFs, setIsFs] = useState(false);
  const [supported, setSupported] = useState(false);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    setSupported(Boolean(document.fullscreenEnabled));
    setStandalone(isStandalone());
    const onChange = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  if (!supported || standalone) return null;

  const toggle = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen({ navigationUI: "hide" });
      }
    } catch { /* user rejected / not allowed */ }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggle}
      className={`gap-1 ${className}`}
      data-testid="fullscreen-toggle"
      title={isFs ? "Exit fullscreen" : "Enter fullscreen"}
    >
      {isFs ? <CornersIn size={16} weight="bold" /> : <CornersOut size={16} weight="bold" />}
    </Button>
  );
};

export default FullscreenToggle;
